import React, { useState, useEffect } from 'react';
import Overview from './Overview';
import Devices from './Devices';
import DeviceDiagnostics from './DeviceDiagnostics';
import RealTimeDeviceManagement from './RealTimeDeviceManagement';
import '../assets/RemoteDebugSessionTabs.css';

// Sub-tab keys that belong to the Device Diagnostics group (used to route
// deep-link prefills from DeviceDetailsTab into that group).
const DIAGNOSTICS_SUBTABS = ['Start', 'Scheduled', 'Active', 'Completed'];

function RemoteDebugSessionTabs({ userInfo, role, allowedTabs, prefilledSystemId, setPrefilledSystemId, setSelectedTab, userGroups = [] }) {
    // Second-level tabs. Overview/Devices stay as siblings; the diagnostics
    // session screens now live under "Device Diagnostics", and the new
    // "Real-Time Device Management" tab sits alongside them.
    const allSubTabs = [
        { key: 'Overview', label: 'Overview' },
        { key: 'Devices', label: 'Devices' },
        { key: 'Device Diagnostics', label: 'Device Diagnostics' },
        { key: 'Real-Time Device Management', label: 'Real-Time Device Management' },
    ];

    // If user has access to Remote Diagnostics, show all sub-tabs
    const subTabs = allowedTabs && allowedTabs.includes('Diagnostics')
        ? allSubTabs
        : [];

    // Set initial active tab to the first available sub-tab
    const [activeSubTab, setActiveSubTab] = useState(subTabs.length > 0 ? subTabs[0].key : 'Overview');

    // Prefill object forwarded down to Device Diagnostics (it interprets .subTab).
    const [diagnosticsPrefill, setDiagnosticsPrefill] = useState(null);
    const [rtdmPrefill, setRtdmPrefill] = useState(null);

    // Handle navigation from DeviceDetailsTab. It emits
    // { systemId, subTab: 'Start' | 'Active' } and switches the top tab to
    // "Remote Diagnostics". Those sub-tabs now live under Device Diagnostics,
    // so route the prefill there and let Device Diagnostics select the sub-tab.
    useEffect(() => {
        if (prefilledSystemId && typeof prefilledSystemId === 'object' && prefilledSystemId.subTab) {
            if (prefilledSystemId.subTab === 'RTDMStart') {
                setActiveSubTab('Real-Time Device Management');
                setRtdmPrefill(prefilledSystemId.systemId);
            } else if (DIAGNOSTICS_SUBTABS.includes(prefilledSystemId.subTab)) {
                setActiveSubTab('Device Diagnostics');
                setDiagnosticsPrefill(prefilledSystemId);
            }
        } else if (prefilledSystemId) {
            // Fallback for a bare string prefilledSystemId → Device Diagnostics / Start.
            setActiveSubTab('Device Diagnostics');
            setDiagnosticsPrefill({ systemId: prefilledSystemId, subTab: 'Start' });
        } else {
            setDiagnosticsPrefill(null);
            setRtdmPrefill(null);
        }
    }, [prefilledSystemId]);

    const renderSubTabContent = () => {
        switch (activeSubTab) {
            case 'Overview':
                return <Overview userInfo={userInfo} role={role} />;
            case 'Devices':
                return <Devices tab="Details" userInfo={userInfo} role={role} setSelectedTab={setSelectedTab} setPrefilledSystemId={setPrefilledSystemId} />;
            case 'Device Diagnostics':
                return <DeviceDiagnostics userInfo={userInfo} role={role} prefilledSystemId={diagnosticsPrefill} setPrefilledSystemId={setPrefilledSystemId} userGroups={userGroups} />;
            case 'Real-Time Device Management':
                return <RealTimeDeviceManagement userInfo={userInfo} role={role} userGroups={userGroups} prefilledSystemId={rtdmPrefill} setPrefilledSystemId={setRtdmPrefill} />;
            default:
                return <Overview userInfo={userInfo} role={role} />;
        }
    };

    return (
        <div className="remote-debug-session-container">
            <div className="sub-tabs-container">
                {subTabs.map((tab) => (
                    <div
                        key={tab.key}
                        className={`sub-tab ${activeSubTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveSubTab(tab.key)}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>
            <div className="sub-tab-content">
                {renderSubTabContent()}
            </div>
        </div>
    );
}

export default RemoteDebugSessionTabs;
